import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Search, UserCheck, UserX, Eye } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AdminUsers = () => {
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  const fetchUsers = useCallback(async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/admin/users`, {
        withCredentials: true
      });
      setUsers(response.data);
    } catch (error) {
      console.error('Failed to fetch users:', error);
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  }, []);

  const filterUsers = useCallback(() => {
    let filtered = users;
    
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(user => 
        user.name?.toLowerCase().includes(term) ||
        user.email?.toLowerCase().includes(term) ||
        user.profile?.username?.toLowerCase().includes(term)
      );
    }
    
    if (statusFilter !== 'all') {
      filtered = filtered.filter(user => {
        if (statusFilter === 'active') return user.is_active !== false;
        if (statusFilter === 'disabled') return user.is_active === false;
        return true;
      });
    }
    
    setFilteredUsers(filtered);
  }, [users, searchTerm, statusFilter]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  useEffect(() => {
    filterUsers();
  }, [filterUsers]);

  const handleToggleStatus = async (userId, currentStatus) => {
    const newStatus = currentStatus === false ? true : false;
    const action = newStatus ? 'enable' : 'disable';
    
    if (!window.confirm(`Are you sure you want to ${action} this user?`)) return;
    
    try {
      await axios.put(
        `${BACKEND_URL}/api/admin/users/${userId}/status`,
        { 
          is_active: newStatus,
          reason: newStatus ? 'Enabled by admin' : 'Disabled by admin'
        },
        { withCredentials: true }
      );
      
      setUsers(users.map(user => 
        user.user_id === userId ? { ...user, is_active: newStatus } : user
      ));
      
      toast.success(`User ${newStatus ? 'enabled' : 'disabled'} successfully`);
    } catch (error) {
      console.error('Failed to update user status:', error);
      toast.error('Failed to update user status');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="w-12 h-12 border-4 border-[#B8935A] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto" data-testid="admin-users">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-[#0A0A0A]">User Management</h1>
        <p className="text-[#525252] mt-1">Manage all registered users</p>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4 mb-6">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#525252]" />
          <Input
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search by name, email, or username..."
            className="pl-10 bg-white border-[#0A0A0A]/10 text-[#0A0A0A]"
            data-testid="user-search-input"
          />
        </div>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="bg-white border border-[#0A0A0A]/10 text-[#0A0A0A] rounded-lg px-4 py-2"
          data-testid="user-status-filter"
        >
          <option value="all">All Users</option>
          <option value="active">Active Only</option>
          <option value="disabled">Disabled Only</option>
        </select>
      </div>

      {/* Users Table */}
      <div className="bg-white border border-[#0A0A0A]/10 rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-[#F5F1EA]">
              <tr>
                <th className="text-left px-6 py-4 text-sm font-semibold text-[#525252]">User</th>
                <th className="text-left px-6 py-4 text-sm font-semibold text-[#525252]">Username</th>
                <th className="text-left px-6 py-4 text-sm font-semibold text-[#525252]">Orders</th>
                <th className="text-left px-6 py-4 text-sm font-semibold text-[#525252]">Subscription</th>
                <th className="text-left px-6 py-4 text-sm font-semibold text-[#525252]">Status</th>
                <th className="text-left px-6 py-4 text-sm font-semibold text-[#525252]">Joined</th>
                <th className="text-left px-6 py-4 text-sm font-semibold text-[#525252]">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#0A0A0A]/10">
              {filteredUsers.map((user) => (
                <tr key={user.user_id} className="hover:bg-[#EBE6DC]/50" data-testid={`user-row-${user.user_id}`}>
                  <td className="px-6 py-4">
                    <div>
                      <p className="text-[#0A0A0A] font-medium">{user.name}</p>
                      <p className="text-sm text-[#525252]">{user.email}</p>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    {user.profile?.username ? (
                      <a 
                        href={`/p/${user.profile.username}`} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-[#B8935A] hover:text-[#B8935A] flex items-center gap-1"
                      >
                        @{user.profile.username}
                        <Eye className="w-4 h-4" />
                      </a>
                    ) : (
                      <span className="text-[#a3a3a3]">No profile</span>
                    )}
                  </td>
                  <td className="px-6 py-4">
                    <span className="text-[#0A0A0A]">{user.order_count || 0}</span>
                  </td>
                  <td className="px-6 py-4">
                    {user.subscription ? (
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        user.subscription.status === 'active' 
                          ? 'bg-green-500/20 text-green-400' 
                          : 'bg-gray-500/20 text-[#525252]'
                      }`}>
                        {user.subscription.status}
                      </span>
                    ) : (
                      <span className="text-[#a3a3a3]">None</span>
                    )}
                  </td>
                  <td className="px-6 py-4">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      user.is_active !== false 
                        ? 'bg-green-500/20 text-green-400' 
                        : 'bg-red-500/20 text-red-400'
                    }`}>
                      {user.is_active !== false ? 'Active' : 'Disabled'}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-sm text-[#525252]">
                    {new Date(user.created_at).toLocaleDateString()}
                  </td>
                  <td className="px-6 py-4">
                    <Button
                      onClick={() => handleToggleStatus(user.user_id, user.is_active)}
                      variant="ghost"
                      size="sm"
                      className={user.is_active !== false 
                        ? 'text-red-400 hover:text-red-300 hover:bg-red-500/10' 
                        : 'text-green-400 hover:text-green-300 hover:bg-green-500/10'
                      }
                      data-testid={`toggle-user-${user.user_id}`}
                    >
                      {user.is_active !== false ? (
                        <>
                          <UserX className="w-4 h-4 mr-1" />
                          Disable
                        </>
                      ) : (
                        <>
                          <UserCheck className="w-4 h-4 mr-1" />
                          Enable
                        </>
                      )}
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        
        {filteredUsers.length === 0 && (
          <div className="text-center py-12">
            <p className="text-[#525252]">No users found</p>
          </div>
        )}
      </div>

      <div className="mt-4 text-sm text-[#525252]">
        Showing {filteredUsers.length} of {users.length} users
      </div>
    </div>
  );
};

export default AdminUsers;
